import { useContext, useState } from "react";

import { toast } from "react-toastify";

import { GlobalContext } from "../../Context/GlobalContext";

const ok = (data) => !data?.responseCode || String(data.responseCode) === "00";

const rows = (data) => {
  const value = data?.response ?? data?.data ?? data?.result ?? [];

  if (Array.isArray(value)) return value;

  return value && typeof value === "object" ? [value] : [];
};

const message = (error, fallback) =>
  error?.response?.data?.responseMessage ||
  error?.response?.data?.message ||
  error?.message ||
  fallback;

export default function useBulkPackages() {
  const { api, anlookApi, userID, getCatalogIdUSCodeCached } =
    useContext(GlobalContext);

 
const resolveBulkCatalogId = async ({ force = false } = {}) => {
  try {
    const usCode = String(userID || "").trim();

    if (!usCode) return "";

    // GlobalContext shared session cache
    if (typeof getCatalogIdUSCodeCached !== "function") {
      throw new Error("getCatalogIdUSCodeCached is missing from GlobalContext");
    }

    const res = await getCatalogIdUSCodeCached(usCode, {
      force,
    });

    if (String(res?.data?.responseCode ?? "") !== "00") {
      toast.error(
        res?.data?.responseMessage || "Failed to fetch catalogue mapping ID",
      );

      return "";
    }

    const response = res?.data?.response;

    const mappingId = Array.isArray(response)
      ? response?.[0]?.mapping_id
      : response?.mapping_id;

    const catalogId = String(mappingId ?? "").trim();

    if (!catalogId) {
      toast.error("Catalogue mapping ID not found");
      return "";
    }

    return catalogId;
  } catch (error) {
    console.error("getCatalogIdUSCode error:", error?.response?.data || error);

    toast.error(
      error?.response?.data?.responseMessage ||
        error?.response?.data?.message ||
        error?.message ||
        "Error fetching catalogue mapping ID",
    );

    return "";
  }
};

  const [packagesLoading, setPackagesLoading] = useState(false);

  const [packageSaving, setPackageSaving] = useState(false);

  const [packageDeletingId, setPackageDeletingId] = useState("");

  const [packageVariantsLoading, setPackageVariantsLoading] = useState(false);

  const [packageVariantSaving, setPackageVariantSaving] = useState(false);

  const [packageVariantDeletingId, setPackageVariantDeletingId] = useState("");

  const [packageItemMappingsLoading, setPackageItemMappingsLoading] =
    useState(false);

  const [packageItemMappingSaving, setPackageItemMappingSaving] =
    useState(false);

  const [packageItemMappingDeletingId, setPackageItemMappingDeletingId] =
    useState("");

  const [packageOptionGroupsLoading, setPackageOptionGroupsLoading] =
    useState(false);

  const [packageOptionGroupSaving, setPackageOptionGroupSaving] =
    useState(false);

  const [packageOptionGroupDeletingId, setPackageOptionGroupDeletingId] =
    useState("");

  const getPackages = async (
    packageId = "",
    catalogueId = "",
    menuId = "",
    parentPackageId = "",
  ) => {
    try {
      setPackagesLoading(true);

      const res = await api.post("/BulkCatalogue/packagesGet", {
        package_id: String(packageId ?? "").trim(),
        catalogue_id:
          String(catalogueId ?? "").trim() || (await resolveBulkCatalogId()),
        fk_menuid: String(menuId ?? "").trim(),
        parent_package_id: String(parentPackageId ?? "").trim(),
      });

      if (!ok(res?.data)) {
        toast.error(res?.data?.responseMessage || "Failed to fetch packages");

        return [];
      }

      return rows(res?.data);
    } catch (error) {
      console.error("packagesGet:", error?.response?.data || error);

      toast.error(message(error, "Error fetching packages"));

      return [];
    } finally {
      setPackagesLoading(false);
    }
  };

  const normalizeIndexFlag = (value) => {
    const normalized = String(value ?? "0")
      .trim()
      .toLowerCase();

    return normalized === "true" || normalized === "1" ? 1 : 0;
  };

  const packagePayload = (form, includeId = false) => ({
    ...(includeId ? { package_id: String(form?.package_id ?? "").trim() } : {}),

    catalogue_id: String(form?.catalogue_id ?? "").trim(),

    fk_menuid: String(form?.fk_menuid ?? "").trim() || null,

    index_sequence: String(form?.index_sequence ?? "").trim(),

    page_from: String(form?.page_from ?? "").trim() || null,

    page_to: String(form?.page_to ?? "").trim() || null,

    is_index_item: normalizeIndexFlag(form?.is_index_item),

    parent_package_id: String(form?.parent_package_id ?? "").trim() || null,

    package_name: String(form?.package_name ?? "").trim(),

    package_type: String(form?.package_type ?? "").trim(),

    description: String(form?.description ?? "").trim(),

    pricing_method: String(form?.pricing_method ?? "").trim(),

    minimum_persons: String(form?.minimum_persons ?? "").trim(),

    maximum_persons: String(form?.maximum_persons ?? "").trim(),

    person_increment: String(form?.person_increment ?? "").trim(),

    base_price: String(form?.base_price ?? "").trim(),

    display_order: String(form?.display_order ?? "").trim(),

    img_url: String(form?.img_url ?? "").trim(),

    status: String(form?.status ?? "").trim(),

    serving_type: String(form?.serving_type ?? "").trim(),
  });

  const uploadPackageImage = async ({ package_name, image_data }) => {
    if (!image_data) return "";

    try {
      const res = await anlookApi.post("/v1/uploadItemImagev2.php", {
        userid: String(userID),
        item_name: String(package_name),
        type: "IMG",
        image_data: String(image_data),
      });

      if (String(res?.data?.status) === "0" && res?.data?.image_url) {
        return String(res.data.image_url).trim();
      }

      toast.error(res?.data?.message || "Image upload failed");
      return "";
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Image upload failed",
      );
      return "";
    }
  };

  const savePackage = async (form, editing) => {
    if (packageSaving) return false;

    const payload = packagePayload(form, editing);

    if (editing && !payload.package_id)
      return (toast.error("Package ID is required"), false);

    if (!payload.package_name)
      return (toast.error("Package name is required"), false);

    try {
      setPackageSaving(true);

      if (form?.image_data) {
        const uploadedUrl = await uploadPackageImage({
          package_name: payload.package_name,
          image_data: form.image_data,
        });

        if (!uploadedUrl) return false;

        payload.img_url = uploadedUrl;
      }

      const res = await api.post(
        editing
          ? "/BulkCatalogue/packagesUpdate"
          : "/BulkCatalogue/packagesInsert",

        payload,
      );

      if (!ok(res?.data)) {
        toast.error(res?.data?.responseMessage || "Package save failed");

        return false;
      }

      toast.success(
        res?.data?.responseMessage ||
          `Package ${editing ? "updated" : "added"} successfully`,
      );

      return true;
    } catch (error) {
      console.error("Package save:", error?.response?.data || error);

      toast.error(message(error, "Error saving package"));

      return false;
    } finally {
      setPackageSaving(false);
    }
  };

  const insertPackage = (form) => savePackage(form, false);

  const updatePackage = (form) => savePackage(form, true);

  const deletePackage = async (packageId) => {
    const id = String(packageId ?? "").trim();

    if (!id || packageDeletingId) return false;

    try {
      setPackageDeletingId(id);

      const res = await api.post("/BulkCatalogue/packagesDelete", {
        package_id: id,
      });

      if (!ok(res?.data)) {
        toast.error(res?.data?.responseMessage || "Package delete failed");

        return false;
      }

      toast.success(
        res?.data?.responseMessage || "Package deleted successfully",
      );

      return true;
    } catch (error) {
      toast.error(message(error, "Error deleting package"));

      return false;
    } finally {
      setPackageDeletingId("");
    }
  };

  const getPackageVariants = async (packageVariantId = "") => {
    try {
      setPackageVariantsLoading(true);

      const res = await api.post("/BulkCatalogue/packageVariantsGet", {
        catalog_id: await resolveBulkCatalogId(),

        package_variant_id: String(packageVariantId ?? "").trim(),
      });

      if (!ok(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Failed to fetch package variants",
        );

        return [];
      }

      return rows(res?.data);
    } catch (error) {
      toast.error(message(error, "Error fetching package variants"));

      return [];
    } finally {
      setPackageVariantsLoading(false);
    }
  };

  const variantPayload = (form, includeId = false) => ({
    ...(includeId
      ? { package_variant_id: String(form?.package_variant_id ?? "").trim() }
      : {}),

    package_id: String(form?.package_id ?? "").trim(),

    variant_name: String(form?.variant_name ?? "").trim(),

    minimum_persons: String(form?.minimum_persons ?? "").trim(),

    maximum_persons: String(form?.maximum_persons ?? "").trim(),

    exact_persons: String(form?.exact_persons ?? "").trim(),

    price: String(form?.price ?? "").trim(),

    status: String(form?.status ?? "").trim(),
  });

  const savePackageVariant = async (form, editing) => {
    if (packageVariantSaving) return false;

    const payload = variantPayload(form, editing);

    if (editing && !payload.package_variant_id)
      return (toast.error("Package Variant ID is required"), false);

    if (!payload.package_id) return (toast.error("Package is required"), false);

    if (!payload.variant_name)
      return (toast.error("Variant name is required"), false);

    try {
      setPackageVariantSaving(true);

      const res = await api.post(
        editing
          ? "/BulkCatalogue/packageVariantsUpdate"
          : "/BulkCatalogue/packageVariantsInsert",

        payload,
      );

      if (!ok(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Package variant save failed",
        );

        return false;
      }

      toast.success(
        res?.data?.responseMessage ||
          `Package variant ${editing ? "updated" : "added"} successfully`,
      );

      return true;
    } catch (error) {
      toast.error(message(error, "Error saving package variant"));

      return false;
    } finally {
      setPackageVariantSaving(false);
    }
  };

  const insertPackageVariant = (form) => savePackageVariant(form, false);

  const updatePackageVariant = (form) => savePackageVariant(form, true);

  const deletePackageVariant = async (packageVariantId) => {
    const id = String(packageVariantId ?? "").trim();

    if (!id || packageVariantDeletingId) return false;

    try {
      setPackageVariantDeletingId(id);

      const res = await api.post("/BulkCatalogue/packageVariantsDelete", {
        package_variant_id: id,
      });

      if (!ok(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Package variant delete failed",
        );

        return false;
      }

      toast.success(
        res?.data?.responseMessage || "Package variant deleted successfully",
      );

      return true;
    } catch (error) {
      toast.error(message(error, "Error deleting package variant"));

      return false;
    } finally {
      setPackageVariantDeletingId("");
    }
  };

  const getPackageItemMappings = async (mappingId = "") => {
    try {
      setPackageItemMappingsLoading(true);

      const res = await api.post("/BulkCatalogue/packageItemMappingGet", {
        catalog_id: await resolveBulkCatalogId(),

        package_item_mapping_id: String(mappingId ?? "").trim(),
      });

      if (!ok(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Failed to fetch package item mappings",
        );

        return [];
      }

      return rows(res?.data);
    } catch (error) {
      toast.error(message(error, "Error fetching package item mappings"));

      return [];
    } finally {
      setPackageItemMappingsLoading(false);
    }
  };

  const mappingPayload = (form, includeId = false) => ({
    ...(includeId
      ? {
          package_item_mapping_id: String(
            form?.package_item_mapping_id ?? "",
          ).trim(),
        }
      : {}),

    package_id: String(form?.package_id ?? "").trim(),

    item_id: String(form?.item_id ?? "").trim(),

    display_order: String(form?.display_order ?? "").trim(),

    status: String(form?.status ?? "").trim(),
  });

  const savePackageItemMapping = async (form, editing) => {
    if (packageItemMappingSaving) return false;

    const payload = mappingPayload(form, editing);

    if (editing && !payload.package_item_mapping_id)
      return (toast.error("Package Item Mapping ID is required"), false);

    if (!payload.package_id) return (toast.error("Package is required"), false);

    if (!payload.item_id) return (toast.error("Item is required"), false);

    try {
      setPackageItemMappingSaving(true);

      const res = await api.post(
        editing
          ? "/BulkCatalogue/packageItemMappingUpdate"
          : "/BulkCatalogue/packageItemMappingInsert",

        payload,
      );

      if (!ok(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Package item mapping save failed",
        );

        return false;
      }

      toast.success(
        res?.data?.responseMessage ||
          `Package item mapping ${editing ? "updated" : "added"} successfully`,
      );

      return true;
    } catch (error) {
      toast.error(message(error, "Error saving package item mapping"));

      return false;
    } finally {
      setPackageItemMappingSaving(false);
    }
  };

  const insertPackageItemMapping = (form) =>
    savePackageItemMapping(form, false);

  const updatePackageItemMapping = (form) => savePackageItemMapping(form, true);

  const deletePackageItemMapping = async (mappingId) => {
    const id = String(mappingId ?? "").trim();

    if (!id || packageItemMappingDeletingId) return false;

    try {
      setPackageItemMappingDeletingId(id);

      const res = await api.post("/BulkCatalogue/packageItemMappingDelete", {
        package_item_mapping_id: id,
      });

      if (!ok(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Package item mapping delete failed",
        );

        return false;
      }

      toast.success(
        res?.data?.responseMessage ||
          "Package item mapping deleted successfully",
      );

      return true;
    } catch (error) {
      toast.error(message(error, "Error deleting package item mapping"));

      return false;
    } finally {
      setPackageItemMappingDeletingId("");
    }
  };

  const getPackageOptionGroups = async (packageOptionGroupId = "") => {
    try {
      setPackageOptionGroupsLoading(true);

      const res = await api.post("/BulkCatalogue/packageOptionGroupsGet", {
        catalog_id: await resolveBulkCatalogId(),

        package_option_group_id: String(packageOptionGroupId ?? "").trim(),
      });

      if (!ok(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Failed to fetch package option groups",
        );

        return [];
      }

      return rows(res?.data);
    } catch (error) {
      toast.error(message(error, "Error fetching package option groups"));

      return [];
    } finally {
      setPackageOptionGroupsLoading(false);
    }
  };

  const packageOptionGroupPayload = (form = {}, includeId = false) => ({
    ...(includeId
      ? {
          package_option_group_id: String(
            form?.package_option_group_id ?? "",
          ).trim(),
        }
      : {}),

    package_id: String(form?.package_id ?? "").trim(),

    option_group_id: String(form?.option_group_id ?? "").trim(),

    group_title: String(form?.group_title ?? "").trim(),

    selection_type: String(form?.selection_type ?? "").trim(),

    minimum_selection: String(form?.minimum_selection ?? "").trim(),

    maximum_selection: String(form?.maximum_selection ?? "").trim(),

    quantity_rule: String(form?.quantity_rule ?? "").trim(),

    is_required: String(form?.is_required ?? "").trim(),

    display_order: String(form?.display_order ?? "").trim(),
  });

  const savePackageOptionGroup = async (form, editing) => {
    if (packageOptionGroupSaving) return false;

    const payload = packageOptionGroupPayload(form, editing);

    if (editing && !payload.package_option_group_id) {
      toast.error("Package Option Group ID is required");

      return false;
    }

    if (!payload.package_id) {
      toast.error("Package is required");

      return false;
    }

    if (!payload.option_group_id) {
      toast.error("Option Group is required");

      return false;
    }

    try {
      setPackageOptionGroupSaving(true);

      const res = await api.post(
        editing
          ? "/BulkCatalogue/packageOptionGroupsUpdate"
          : "/BulkCatalogue/packageOptionGroupsInsert",

        payload,
      );

      if (!ok(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Package option group save failed",
        );

        return false;
      }

      toast.success(
        res?.data?.responseMessage ||
          `Package option group ${editing ? "updated" : "added"} successfully`,
      );

      return true;
    } catch (error) {
      toast.error(message(error, "Error saving package option group"));

      return false;
    } finally {
      setPackageOptionGroupSaving(false);
    }
  };

  const insertPackageOptionGroup = (form) =>
    savePackageOptionGroup(form, false);

  const updatePackageOptionGroup = (form) => savePackageOptionGroup(form, true);

  const deletePackageOptionGroup = async (packageOptionGroupId) => {
    const id = String(packageOptionGroupId ?? "").trim();

    if (!id || packageOptionGroupDeletingId) return false;

    try {
      setPackageOptionGroupDeletingId(id);

      const res = await api.post("/BulkCatalogue/packageOptionGroupsDelete", {
        package_option_group_id: id,
      });

      if (!ok(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Package option group delete failed",
        );

        return false;
      }

      toast.success(
        res?.data?.responseMessage ||
          "Package option group deleted successfully",
      );

      return true;
    } catch (error) {
      toast.error(message(error, "Error deleting package option group"));

      return false;
    } finally {
      setPackageOptionGroupDeletingId("");
    }
  };

  return {
    packagesLoading,

    packageSaving,

    packageDeletingId,

    getPackages,

    insertPackage,

    updatePackage,

    deletePackage,

    packageVariantsLoading,

    packageVariantSaving,

    packageVariantDeletingId,

    getPackageVariants,

    insertPackageVariant,

    updatePackageVariant,

    deletePackageVariant,

    packageItemMappingsLoading,

    packageItemMappingSaving,

    packageItemMappingDeletingId,

    getPackageItemMappings,

    insertPackageItemMapping,

    updatePackageItemMapping,

    deletePackageItemMapping,

    packageOptionGroupsLoading,

    packageOptionGroupSaving,

    packageOptionGroupDeletingId,

    getPackageOptionGroups,

    insertPackageOptionGroup,

    updatePackageOptionGroup,

    deletePackageOptionGroup,
  };
}
